import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import OperisLoader from '../../components/common/OperisLoader';
import { DashboardPageSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import {
  BarChart3, Clock, Users, CheckCircle, AlertTriangle, TrendingUp,
  ChevronRight, ChevronLeft, ArrowLeft, RefreshCw, Factory,
  Timer, Activity, XCircle, Briefcase, Calendar, Target, Award, Zap,
  Play, Square, FileText, Package, Database, ChevronDown, ChevronUp,
  Pause, Hash, Brain
} from 'lucide-react';
import { getKpiDashboard, getTeamKpi, getMemberKpi } from '../../api/kpi.api';

/* ═══════════════════════════════════════════════════════════ */
/*  KPI Dashboard — Enhanced with rich drill-downs             */
/* ═══════════════════════════════════════════════════════════ */
const KpiDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillTeam, setDrillTeam] = useState(null);
  const [drillMember, setDrillMember] = useState(null);
  const [teamDetail, setTeamDetail] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [expandedSection, setExpandedSection] = useState({});

  const toggleSection = (key) => setExpandedSection(prev => ({ ...prev, [key]: !prev[key] }));

  const buildParams = useCallback(() => {
    const p = {};
    if (dateRange.startDate) p.startDate = dateRange.startDate;
    if (dateRange.endDate) p.endDate = dateRange.endDate;
    return p;
  }, [dateRange]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getKpiDashboard(buildParams());
      setData(res.data?.data);
    } catch (err) { console.error('KPI fetch error:', err); }
    setLoading(false);
  }, [buildParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Drill navigation ─── */
  const openTeamDrill = async (team) => {
    setDrillTeam(team);
    setDrillMember(null);
    setMemberDetail(null);
    setExpandedSection({});
    setDrillLoading(true);
    try {
      const res = await getTeamKpi(team.teamId, buildParams());
      setTeamDetail(res.data?.data);
    } catch (err) { console.error('Team drill error:', err); setTeamDetail(null); }
    setDrillLoading(false);
  };

  const openMemberDrill = async (member) => {
    setDrillMember(member);
    setExpandedSection({ runningEntries: true });
    setDrillLoading(true);
    try {
      const res = await getMemberKpi(member.userId, buildParams());
      setMemberDetail(res.data?.data);
    } catch (err) { console.error('Member drill error:', err); setMemberDetail(null); }
    setDrillLoading(false);
  };

  const goBack = () => {
    if (drillMember) { setDrillMember(null); setMemberDetail(null); setExpandedSection({}); return; }
    if (drillTeam) { setDrillTeam(null); setTeamDetail(null); setExpandedSection({}); return; }
  };

  const breadcrumb = () => {
    const parts = [{ label: 'Overview', onClick: () => { setDrillTeam(null); setDrillMember(null); setTeamDetail(null); setMemberDetail(null); setExpandedSection({}); } }];
    if (drillTeam) parts.push({ label: drillTeam.teamName, onClick: () => { setDrillMember(null); setMemberDetail(null); setExpandedSection({}); } });
    if (drillMember) parts.push({ label: drillMember.fullName, onClick: () => {} });
    return parts;
  };

  /* ─── Mini components ─── */
  const KpiCard = ({ icon: Icon, label, value, sub, color = 'indigo', trend, onClick }) => (
    <div onClick={onClick} className={`bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/50 hover:shadow-md transition-all group ${onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-center justify-between mb-1">
        <div className={`p-2 rounded-xl bg-${color}-50 dark:bg-${color}-500/10 group-hover:scale-110 transition-transform`}>
          <Icon className={`w-4 h-4 text-${color}-500`} />
        </div>
        {trend !== undefined && trend !== null && (
          <span className={`text-[10px] font-bold ${trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-400'}`}>
            {trend > 0 ? '↑' : trend < 0 ? '↓' : '—'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{value ?? 0}</p>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );

  const MiniBar = ({ items, total }) => (
    <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
      {items.map((item, i) => (
        <div key={i} className="h-full transition-all" style={{ width: `${total > 0 ? (item.count / total * 100) : 0}%`, backgroundColor: item.color }} title={`${item.label}: ${item.count}`} />
      ))}
    </div>
  );

  const StatusPill = ({ label, count, color }) => (
    <div className="flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-xs font-bold text-gray-700 dark:text-gray-300 ml-auto">{count}</span>
    </div>
  );

  const SyncBadge = ({ status }) => {
    const cfg = { Synced: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400', Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400', Failed: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg[status] || cfg.Pending}`}>{status || 'Pending'}</span>;
  };

  const CollapsibleSection = ({ id, title, icon: Icon, count, color = 'indigo', children, defaultOpen }) => {
    const isOpen = expandedSection[id] ?? defaultOpen;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden">
        <button onClick={() => toggleSection(id)} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl bg-${color}-50 dark:bg-${color}-500/10`}><Icon className={`w-4 h-4 text-${color}-500`} /></div>
            <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{title}</span>
            {count !== undefined && <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-[10px] font-bold text-gray-600 dark:text-gray-400">{count}</span>}
          </div>
          {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {isOpen && <div className="px-4 pb-4">{children}</div>}
      </div>
    );
  };

  const DailyChart = ({ data: chartData, label = 'Hours' }) => {
    if (!chartData?.length) return <p className="text-xs text-gray-400 text-center py-4">No data for chart</p>;
    const max = Math.max(...chartData.map(d => d.value), 1);
    return (
      <div>
        <div className="flex items-end gap-0.5 h-32">
          {chartData.slice(-30).map((p, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
              <div className="w-full bg-indigo-400 dark:bg-indigo-500 rounded-t transition-all hover:bg-indigo-500 dark:hover:bg-indigo-400"
                style={{ height: `${(p.value / max) * 100}%`, minHeight: p.value > 0 ? '2px' : '0' }} />
              <div className="absolute -top-8 bg-gray-900 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value} {label}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[9px] text-gray-400">30 days ago</span>
          <span className="text-[9px] text-gray-400">Today</span>
        </div>
      </div>
    );
  };

  const ClipboardList = Target; // reuse icon

  /* ═══════════════════════════════════════════════════════ */
  /*  OVERVIEW LEVEL                                         */
  /* ═══════════════════════════════════════════════════════ */
  const renderOverview = () => {
    const s = data.summary;
    return (
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <KpiCard icon={Clock} label="Total Hours" value={s.totalHoursLogged?.toFixed(1)} sub={`${s.avgHoursPerDay?.toFixed(1)}h avg/day`} color="blue" />
          <KpiCard icon={Activity} label="Running Timers" value={s.ongoingTimers} color="amber" />
          <KpiCard icon={Target} label="Tasks Completed" value={s.tasksCompleted} sub={`of ${s.totalTaskAssignments} total`} color="green" />
          <KpiCard icon={AlertTriangle} label="Overdue Tasks" value={s.tasksOverdue} color="red" />
          <KpiCard icon={Zap} label="On-time Rate" value={`${s.onTimeCompletionRate}%`} color="purple" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <KpiCard icon={Users} label="Teams" value={s.totalTeams} sub={`${s.totalMembers} members`} color="indigo" />
          <KpiCard icon={Factory} label="Production Lots" value={s.totalProductionLots} sub={`${s.activeProductionLots} active`} color="slate" />
          <KpiCard icon={RefreshCw} label="Sync Rate" value={`${s.syncSuccessRate}%`} sub={`${s.failedSyncEntries} failed`} color="emerald" />
          <KpiCard icon={Briefcase} label="Prod Tasks" value={s.totalProductionTasks} color="cyan" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Production Lots by Status</h3>
            <MiniBar items={data.production?.lotsByStatus || []} total={s.totalProductionLots} />
            <div className="mt-3 space-y-1.5">
              {data.production?.lotsByStatus?.map((sl, i) => <StatusPill key={i} label={sl.label} count={sl.count} color={sl.color} />)}
            </div>
            {data.production?.longestRunningTaskName && (
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-500/10 rounded-xl">
                <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase">Longest Running</p>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-0.5">{data.production.longestRunningTaskName}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{data.production.longestRunningTaskDays} days</p>
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Activity Sync Status</h3>
            <MiniBar items={data.production?.activitiesBySync || []} total={(data.production?.activitiesBySync || []).reduce((a, b) => a + b.count, 0)} />
            <div className="mt-3 space-y-1.5">
              {data.production?.activitiesBySync?.map((sl, i) => <StatusPill key={i} label={sl.label} count={sl.count} color={sl.color} />)}
            </div>
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mt-5 mb-3">Task Status</h3>
            <MiniBar items={data.production?.tasksByStatus || []} total={s.totalProductionTasks} />
            <div className="mt-3 space-y-1.5">
              {data.production?.tasksByStatus?.map((sl, i) => <StatusPill key={i} label={sl.label} count={sl.count} color={sl.color} />)}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Daily Hours (Last 30 days)</h3>
            <DailyChart data={data.timeline?.dailyHours} label="h" />
          </div>
        </div>

        {/* Team Cards */}
        <div className="mt-4">
          <h2 className="text-base font-bold text-gray-800 dark:text-white mb-3">Team Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.teams?.map((team, i) => (
              <button key={i} onClick={() => openTeamDrill(team)}
                className="text-left bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 hover:border-indigo-300 dark:hover:border-indigo-500/30 hover:shadow-lg transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{team.teamName}</h3>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{team.department || 'No dept'} · {team.memberCount} members</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{team.completionRate}%</p>
                    <p className="text-[9px] text-gray-400 uppercase">Complete</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <div className="text-center"><p className="text-sm font-bold text-gray-700 dark:text-gray-300">{team.totalTasks}</p><p className="text-[9px] text-gray-400">Tasks</p></div>
                  <div className="text-center"><p className="text-sm font-bold text-green-500">{team.completedTasks}</p><p className="text-[9px] text-gray-400">Done</p></div>
                  <div className="text-center"><p className="text-sm font-bold text-blue-500">{team.inProgressTasks}</p><p className="text-[9px] text-gray-400">Active</p></div>
                  <div className="text-center"><p className="text-sm font-bold text-red-500">{team.overdueTasks}</p><p className="text-[9px] text-gray-400">Overdue</p></div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">{team.totalHours?.toFixed(1)}h total · {team.avgHoursPerMember?.toFixed(1)}h/member</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  };

  /* ═══════════════════════════════════════════════════════ */
  /*  TEAM DRILL-DOWN (enhanced)                             */
  /* ═══════════════════════════════════════════════════════ */
  const renderTeamDrill = () => {
    const t = teamDetail || drillTeam;
    return (
      <>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">{t.teamName}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Lead: {t.leadName} {t.pmName ? `· PM: ${t.pmName}` : ''} · {t.department}</p>
            </div>
            <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{t.completionRate}%</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
          <KpiCard icon={Users} label="Members" value={t.memberCount} color="indigo" />
          <KpiCard icon={ClipboardList} label="Total Tasks" value={t.totalTasks} color="blue" />
          <KpiCard icon={CheckCircle} label="Completed" value={t.completedTasks} color="green" />
          <KpiCard icon={Activity} label="In Progress" value={t.inProgressTasks} color="amber" />
          <KpiCard icon={AlertTriangle} label="Overdue" value={t.overdueTasks} color="red" />
          <KpiCard icon={Clock} label="Total Hours" value={t.totalHours?.toFixed(1)} color="purple" />
        </div>

        {/* New: extra stats row from team detail */}
        {teamDetail && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KpiCard icon={Timer} label="Running Timers" value={teamDetail.ongoingTimers} color="amber" />
            <KpiCard icon={Hash} label="Time Entries" value={teamDetail.totalTimeEntries} color="cyan" />
            <KpiCard icon={Package} label="Prod Tasks" value={teamDetail.productionTasksAssigned} color="slate" />
            <KpiCard icon={Factory} label="Prod Lots" value={teamDetail.productionLotsLinked} color="violet" />
          </div>
        )}

        {/* Sync status bar */}
        {teamDetail && (teamDetail.syncedEntries > 0 || teamDetail.pendingSyncEntries > 0 || teamDetail.failedSyncEntries > 0) && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/50 mb-4">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">SAP Sync Status</h3>
            <MiniBar items={[
              { label: 'Synced', count: teamDetail.syncedEntries, color: '#22c55e' },
              { label: 'Pending', count: teamDetail.pendingSyncEntries, color: '#f59e0b' },
              { label: 'Failed', count: teamDetail.failedSyncEntries, color: '#ef4444' },
            ]} total={teamDetail.syncedEntries + teamDetail.pendingSyncEntries + teamDetail.failedSyncEntries} />
            <div className="flex gap-4 mt-2">
              <StatusPill label="Synced" count={teamDetail.syncedEntries} color="#22c55e" />
              <StatusPill label="Pending" count={teamDetail.pendingSyncEntries} color="#f59e0b" />
              <StatusPill label="Failed" count={teamDetail.failedSyncEntries} color="#ef4444" />
            </div>
          </div>
        )}

        {/* Timeline chart */}
        {teamDetail?.timeline?.dailyHours?.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 mb-4">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Daily Hours (Last 30 days)</h3>
            <DailyChart data={teamDetail.timeline.dailyHours} label="h" />
          </div>
        )}

        {/* Members */}
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Team Members</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(t.members || []).map((m, i) => (
            <button key={i} onClick={() => openMemberDrill(m)}
              className="text-left bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/50 hover:border-indigo-300 dark:hover:border-indigo-500/30 hover:shadow-md transition-all group">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-sm font-bold text-gray-800 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400">{m.fullName}</h4>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">{m.email}</p>
                </div>
                {m.runningTimers > 0 && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 animate-pulse">
                    <Timer className="w-3 h-3" /> Live
                  </span>
                )}
              </div>
              <div className="grid grid-cols-5 gap-2 mb-2">
                <div className="text-center p-1.5 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                  <p className="text-sm font-bold text-gray-700 dark:text-gray-300">{m.totalTasks}</p>
                  <p className="text-[8px] text-gray-400 uppercase">Tasks</p>
                </div>
                <div className="text-center p-1.5 bg-green-50 dark:bg-green-500/10 rounded-lg">
                  <p className="text-sm font-bold text-green-600">{m.completedTasks}</p>
                  <p className="text-[8px] text-gray-400 uppercase">Done</p>
                </div>
                <div className="text-center p-1.5 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
                  <p className="text-sm font-bold text-blue-600">{m.inProgressTasks}</p>
                  <p className="text-[8px] text-gray-400 uppercase">Active</p>
                </div>
                <div className="text-center p-1.5 bg-indigo-50 dark:bg-indigo-500/10 rounded-lg">
                  <p className="text-sm font-bold text-indigo-600">{m.hoursLogged?.toFixed(1)}</p>
                  <p className="text-[8px] text-gray-400 uppercase">Hours</p>
                </div>
                <div className="text-center p-1.5 bg-cyan-50 dark:bg-cyan-500/10 rounded-lg">
                  <p className="text-sm font-bold text-cyan-600">{m.timeEntries || 0}</p>
                  <p className="text-[8px] text-gray-400 uppercase">Entries</p>
                </div>
              </div>
              {m.currentTask && <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">🔧 {m.currentTask}</p>}
            </button>
          ))}
        </div>
      </>
    );
  };

  /* ═══════════════════════════════════════════════════════ */
  /*  MEMBER DRILL-DOWN (fully enhanced)                     */
  /* ═══════════════════════════════════════════════════════ */
  const renderMemberDrill = () => {
    const m = memberDetail || drillMember;

    if (drillLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <OperisLoader size="md" message="Loading member details…" />
        </div>
      );
    }

    return (
      <>
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">{m.fullName}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{m.email} · {m.department} {m.role ? `· ${m.role}` : ''}</p>
            </div>
            {(m.runningTimers > 0) && (
              <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-50 dark:bg-green-500/10 text-green-600 animate-pulse">
                <Timer className="w-4 h-4" /> {m.runningTimers} Timer{m.runningTimers > 1 ? 's' : ''} Running
              </span>
            )}
          </div>
        </div>

        {/* Stats Row 1: Tasks */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <KpiCard icon={Target} label="Total Tasks" value={m.totalTasks} color="indigo" />
          <KpiCard icon={CheckCircle} label="Completed" value={m.completedTasks} color="green" />
          <KpiCard icon={Activity} label="In Progress" value={m.inProgressTasks} color="amber" />
          <KpiCard icon={Pause} label="Paused" value={m.pausedTasks ?? 0} color="slate" />
          <KpiCard icon={AlertTriangle} label="Overdue" value={m.overdueTasks} color="red" />
        </div>

        {/* Stats Row 2: Time */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <KpiCard icon={Clock} label="Total Hours" value={m.totalHoursLogged?.toFixed(1) ?? m.hoursLogged?.toFixed(1)} color="blue" />
          <KpiCard icon={BarChart3} label="Avg Hours/Day" value={m.avgHoursPerDay?.toFixed(1) ?? '0.0'} color="purple" />
          <KpiCard icon={Hash} label="Time Entries" value={m.totalTimeEntries ?? m.timeEntries ?? 0} color="cyan" />
          <KpiCard icon={Timer} label="Running Timers" value={m.runningTimers ?? 0} color="green" />
          <KpiCard icon={Square} label="Stopped Today" value={m.stoppedEntriesToday ?? 0} color="orange" />
        </div>

        {/* Stats Row 3: Production & Sync */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <KpiCard icon={Package} label="Prod Tasks" value={m.productionTasksAssigned ?? 0} color="violet" />
          <KpiCard icon={Factory} label="Prod Lots" value={m.productionLotsLinked ?? 0} color="slate" />
          <KpiCard icon={Zap} label="Task Completion" value={`${m.taskCompletionRate ?? 0}%`} color="emerald" />
          <KpiCard icon={RefreshCw} label="Sync Status" value={`${m.syncedEntries ?? 0}/${(m.syncedEntries ?? 0) + (m.pendingSyncEntries ?? 0) + (m.failedSyncEntries ?? 0)}`} sub={m.failedSyncEntries > 0 ? `${m.failedSyncEntries} failed` : 'All good'} color={m.failedSyncEntries > 0 ? 'red' : 'emerald'} />
        </div>

        {/* SAP Sync Bar */}
        {memberDetail && (memberDetail.syncedEntries > 0 || memberDetail.pendingSyncEntries > 0 || memberDetail.failedSyncEntries > 0) && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/50 mb-4">
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">SAP Sync Breakdown</h3>
            <MiniBar items={[
              { label: 'Synced', count: memberDetail.syncedEntries, color: '#22c55e' },
              { label: 'Pending', count: memberDetail.pendingSyncEntries, color: '#f59e0b' },
              { label: 'Failed', count: memberDetail.failedSyncEntries, color: '#ef4444' },
            ]} total={memberDetail.syncedEntries + memberDetail.pendingSyncEntries + memberDetail.failedSyncEntries} />
            <div className="flex gap-4 mt-2">
              <StatusPill label="Synced" count={memberDetail.syncedEntries} color="#22c55e" />
              <StatusPill label="Pending" count={memberDetail.pendingSyncEntries} color="#f59e0b" />
              <StatusPill label="Failed" count={memberDetail.failedSyncEntries} color="#ef4444" />
            </div>
          </div>
        )}

        {/* ── Running Entries ── */}
        {memberDetail?.runningEntries?.length > 0 && (
          <CollapsibleSection id="runningEntries" title="Running Timers" icon={Play} count={memberDetail.runningEntries.length} color="green" defaultOpen>
            <div className="space-y-2">
              {memberDetail.runningEntries.map((e, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-500/10 rounded-xl border border-green-200 dark:border-green-500/20">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Play className="w-3 h-3 text-green-500 animate-pulse" />
                      <span className="text-sm font-medium text-gray-800 dark:text-white truncate">{e.description || e.activityName || 'Timer Active'}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Started {e.startedAt || e.startTime || '—'} · {e.productionTaskId ? `Task: ${e.productionTaskId}` : 'No production task'}
                    </p>
                  </div>
                  <div className="text-right ml-3">
                    <p className="text-sm font-bold text-green-600 dark:text-green-400">{e.hours?.toFixed(2)}h</p>
                    <SyncBadge status={e.syncStatus} />
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* ── Recent Time Entries ── */}
        {memberDetail?.recentTimeEntries?.length > 0 && (
          <div className="mt-4">
            <CollapsibleSection id="recentEntries" title="Recent Time Entries" icon={Clock} count={memberDetail.recentTimeEntries.length} color="blue">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Date</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Start</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">End</th>
                      <th className="text-right py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Hours</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Activity</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Prod Task</th>
                      <th className="text-center py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-center py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Sync</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberDetail.recentTimeEntries.map((e, i) => (
                      <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/20">
                        <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400">{e.startTime || '—'}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400">{e.endTime || '—'}</td>
                        <td className="py-2 px-2 text-right font-bold text-gray-700 dark:text-gray-300">{e.hours?.toFixed(2)}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400 truncate max-w-[120px]">{e.activityName || e.description || '—'}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400 truncate max-w-[100px]">{e.productionTaskId || '—'}</td>
                        <td className="py-2 px-2 text-center">
                          {e.isRunning ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400">
                              <Play className="w-2.5 h-2.5" /> Running
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                              <Square className="w-2.5 h-2.5" /> Stopped
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center"><SyncBadge status={e.syncStatus} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* ── Task Assignments ── */}
        {memberDetail?.taskAssignments?.length > 0 && (
          <div className="mt-4">
            <CollapsibleSection id="taskAssignments" title="Task Assignments" icon={ClipboardList} count={memberDetail.taskAssignments.length} color="indigo">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Task</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Prod Lot</th>
                      <th className="text-center py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Priority</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Due Date</th>
                      <th className="text-right py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Est. Hrs</th>
                      <th className="text-right py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Act. Hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberDetail.taskAssignments.map((a, i) => (
                      <tr key={i} className={`border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/20 ${a.isOverdue ? 'bg-red-50/50 dark:bg-red-500/5' : ''}`}>
                        <td className="py-2 px-2 text-gray-700 dark:text-gray-300 font-medium truncate max-w-[150px]">{a.taskTitle}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400 truncate max-w-[120px]">{a.productionLotName || a.productionLotId || '—'}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            a.status === 'Completed' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' :
                            a.status === 'In Progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' :
                            a.status === 'Paused' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' :
                            a.status === 'Cancelled' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                          }`}>{a.status}</span>
                        </td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400">{a.priority}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400">
                          {a.dueDate ? new Date(a.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                          {a.isOverdue && <AlertTriangle className="w-3 h-3 text-red-500 inline ml-1" />}
                        </td>
                        <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400">{a.estimatedHours?.toFixed(1) ?? '—'}</td>
                        <td className="py-2 px-2 text-right font-bold text-gray-700 dark:text-gray-300">{a.actualHours?.toFixed(1) ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* ── Production Tasks ── */}
        {memberDetail?.productionTasks?.length > 0 && (
          <div className="mt-4">
            <CollapsibleSection id="productionTasks" title="Production Tasks" icon={Factory} count={memberDetail.productionTasks.length} color="violet">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Task ID</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Lot</th>
                      <th className="text-left py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Description</th>
                      <th className="text-center py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-right py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Entries</th>
                      <th className="text-right py-2 px-2 font-bold text-gray-600 dark:text-gray-400">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberDetail.productionTasks.map((pt, i) => (
                      <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/20">
                        <td className="py-2 px-2 text-gray-700 dark:text-gray-300 font-mono text-[10px]">{pt.productionTaskId}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400 font-mono text-[10px]">{pt.productionLotId}</td>
                        <td className="py-2 px-2 text-gray-600 dark:text-gray-400 truncate max-w-[150px]">{pt.lotDescription || '—'}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            pt.status === '5' ? 'bg-green-100 text-green-700 dark:bg-green-500/20' :
                            pt.status === '3' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20' :
                            pt.status === '2' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-700'
                          }`}>{pt.statusDescription}</span>
                        </td>
                        <td className="py-2 px-2 text-right text-gray-600 dark:text-gray-400">{pt.timeEntriesCount}</td>
                        <td className="py-2 px-2 text-right font-bold text-gray-700 dark:text-gray-300">{pt.totalHoursOnTask?.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* ── Timeline Charts ── */}
        {memberDetail?.timeline && (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {memberDetail.timeline.dailyHours?.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Daily Hours (Last 30 days)</h3>
                <DailyChart data={memberDetail.timeline.dailyHours} label="h" />
              </div>
            )}
            {memberDetail.timeline.dailyEntries?.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Daily Entries Count</h3>
                <DailyChart data={memberDetail.timeline.dailyEntries} label="entries" />
              </div>
            )}
            {memberDetail.timeline.tasksCompletedOverTime?.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Tasks Completed Over Time</h3>
                <DailyChart data={memberDetail.timeline.tasksCompletedOverTime} label="tasks" />
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  /* ═══════════════════════════════════════════════════════ */
  /*  MAIN RENDER                                            */
  /* ═══════════════════════════════════════════════════════ */
  if (loading) {
    return <DashboardPageSkeleton />;
  }

  if (!data) {
    return (
      <div className="text-center py-24">
        <BarChart3 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">No Data Available</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">KPI data could not be loaded.</p>
        <button onClick={fetchData} className="mt-3 px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm hover:bg-indigo-600 transition-colors">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageFadeSection>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {(drillTeam || drillMember) && (
            <button onClick={goBack} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">KPI Dashboard</h1>
            <div className="flex items-center gap-1 mt-0.5">
              {breadcrumb().map((b, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRight className="w-3 h-3 text-gray-400" />}
                  <button onClick={b.onClick} className={`text-xs ${i === breadcrumb().length - 1 ? 'text-indigo-600 dark:text-indigo-400 font-semibold' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}>{b.label}</button>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/50 px-3 py-1.5 rounded-full">
            {data.viewerRole} View — {data.viewerName}
          </span>
          <input type="date" value={dateRange.startDate} onChange={(e) => setDateRange(d => ({ ...d, startDate: e.target.value }))}
            className="px-2 py-1.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300" />
          <input type="date" value={dateRange.endDate} onChange={(e) => setDateRange(d => ({ ...d, endDate: e.target.value }))}
            className="px-2 py-1.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300" />
          <button onClick={fetchData} className="p-2 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/kpi-ai-analytics')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-medium shadow-md shadow-indigo-500/25 hover:shadow-lg hover:shadow-indigo-500/30 transition-all">
            <Brain className="w-3.5 h-3.5" /> AI Analytics
          </button>
        </div>
      </div>

      </PageFadeSection>
      <PageFadeSection>
      {/* Content */}
      {drillMember ? renderMemberDrill() : drillTeam ? renderTeamDrill() : renderOverview()}
      </PageFadeSection>
    </div>
  );
};

export default KpiDashboardPage;
